import { type PropsWithChildren, useMemo } from "react";
import { useNavigate } from "react-router";
import {
  BarChartIcon,
  Chat_1Icon,
  ConnectionIcon,
  Logout01Icon,
  NotificationIcon,
  Setting_1Icon,
} from "vega-icons";

import { useAuth } from "@vega-api/custom/api/hooks/useAuth";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { FrontendPortal } from "@vega-api/generated/types/frontendPortal";
import { MenuItemType } from "@vega-core/components/Deprecated/DropdownMenu/MenuItems.type";
import {
  LayoutContext,
  type LayoutContextProps,
} from "@vega-core/components/Layout/LayoutContext";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { m } from "@vega-core/paraglide/messages";
import { MobileNavigation } from "@vega-modules/components/MobileNavigation/MobileNavigation";
import { CoInvestmentFullPaths } from "@vega-modules/paths";
import { NotificationNavBadge } from "@vega-modules/scenes/CoInvestments/components/NotificationNavBadge/NotificationNavBadge";
import { COINVEST_NOTIFICATION_PUBLISHER } from "@vega-modules/scenes/CoInvestments/constants/notificationConstants";
import { useUnreadMessagesTotal } from "@vega-modules/scenes/CoInvestments/messaging/conversationsData";
import { useScopedUnreadCount } from "@vega-modules/scenes/Notifications/useScopedUnreadCount";

const source = FrontendPortal.COINVEST_WORKBENCH_PORTAL;

export function GpCoInvestLayoutContextProvider({
  children,
}: PropsWithChildren) {
  const { isLoading, logout } = useAuth();
  const { featureFlag } = useFeatureFlag();
  const navigate = useNavigate();

  const settingsPath = "/settings/profile";
  const isInsightsEnabled = featureFlag[FeatureFlag.COINVEST_INSIGHTS_VD_26155];
  const isNotificationsEnabled =
    featureFlag[FeatureFlag.COINVEST_NOTIFICATIONS];
  const isMessagingEnabled =
    featureFlag[FeatureFlag.COINVEST_MESSAGING_VD_28584];

  const notificationsCount = useScopedUnreadCount(
    { publisher: COINVEST_NOTIFICATION_PUBLISHER },
    { enabled: isNotificationsEnabled },
  );
  const messagesCount = useUnreadMessagesTotal({
    enabled: isMessagingEnabled,
  });

  const navItem: LayoutContextProps = useMemo(
    () => ({
      navigationItems: getNavigationItems({
        isInsightsEnabled,
        isNotificationsEnabled,
        isMessagingEnabled,
        notificationsCount,
        messagesCount,
      }),
      secondaryMenuItems: [
        [
          {
            action: () => {
              navigate(settingsPath);
            },
            href: settingsPath,
            icon: <Setting_1Icon />,
            title: m.titles_settings(),
          },
        ],
        [
          {
            action: async () =>
              logout({
                isGlobal: true,
                savePath: false,
                source,
              }),
            icon: <Logout01Icon />,
            title: m.titles_signOut(),
            type: MenuItemType.Button,
          },
        ],
      ],
    }),
    [
      isInsightsEnabled,
      isNotificationsEnabled,
      isMessagingEnabled,
      notificationsCount,
      messagesCount,
      logout,
      navigate,
      settingsPath,
    ],
  );

  return (
    <LayoutContext.Provider value={navItem}>
      <MobileNavigation logoutSource={source} />
      {isLoading ? <LoadingSpinner /> : null}
      {children}
    </LayoutContext.Provider>
  );
}

function getNavigationItems({
  isInsightsEnabled,
  isNotificationsEnabled,
  isMessagingEnabled,
  notificationsCount,
  messagesCount,
}: {
  isInsightsEnabled: boolean;
  isNotificationsEnabled: boolean;
  isMessagingEnabled: boolean;
  notificationsCount: number;
  messagesCount: number;
}) {
  const navItems: LayoutContextProps["navigationItems"] = [];

  navItems.push({
    id: "co-investment",
    href: CoInvestmentFullPaths.CoInvestments,
    icon: ConnectionIcon,
    name: m.navTitles_coInvestments(),
    isActive: (pathname: string) =>
      pathname.startsWith(CoInvestmentFullPaths.CoInvestments) &&
      !pathname.startsWith(CoInvestmentFullPaths.CoInvestmentInsights) &&
      !pathname.startsWith(CoInvestmentFullPaths.CoInvestmentNotifications) &&
      !pathname.startsWith(CoInvestmentFullPaths.CoInvestmentMessages),
    analyticsSelector: "gtm-nav-co-investment",
  });

  if (isInsightsEnabled) {
    navItems.push({
      id: "co-investment-insights",
      href: CoInvestmentFullPaths.CoInvestmentInsights,
      icon: BarChartIcon,
      name: m.coInvestments_nav_insights(),
      analyticsSelector: "gtm-nav-co-investment-insights",
    });
  }

  if (isNotificationsEnabled) {
    navItems.push({
      id: "co-investment-notifications",
      href: CoInvestmentFullPaths.CoInvestmentNotifications,
      icon: NotificationIcon,
      name: m.coInvestments_nav_notifications(),
      rightIcon: ({ isCollapsed }) => (
        <NotificationNavBadge
          count={notificationsCount}
          isCollapsed={isCollapsed}
        />
      ),
      analyticsSelector: "gtm-nav-co-investment-notifications",
    });
  }

  if (isMessagingEnabled) {
    navItems.push({
      id: "co-investment-messages",
      href: CoInvestmentFullPaths.CoInvestmentMessages,
      icon: Chat_1Icon,
      name: m.coInvestments_nav_messages(),
      rightIcon: ({ isCollapsed }) => (
        <NotificationNavBadge count={messagesCount} isCollapsed={isCollapsed} />
      ),
      analyticsSelector: "gtm-nav-co-investment-messages",
    });
  }

  return navItems;
}
